import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/api/authApi'
import { SiteShell } from '@/components/layout/SiteShell'
import { morphToast } from '@/components/ui/Toast'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { PasswordInput } from '@/components/ui/PasswordInput'

type PasswordStep = null | 'verify' | 'change'

export default function ProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const updateProfile = useAuthStore((s) => s.updateProfile)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [profileSubmitting, setProfileSubmitting] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)

  const [passwordStep, setPasswordStep] = useState<PasswordStep>(null)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordSubmitting, setPasswordSubmitting] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      setName(user.name ?? '')
      setEmail(user.email ?? '')
    }
  }, [user])

  function cancelPasswordChange() {
    setPasswordStep(null)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError(null)
  }

  async function handleProfileSubmit(e: FormEvent) {
    e.preventDefault()
    setProfileError(null)
    setProfileSubmitting(true)
    try {
      const body: { name?: string; email?: string } = {}
      if (name && name !== user?.name) body.name = name
      if (email && email !== user?.email) body.email = email
      if (Object.keys(body).length === 0) {
        morphToast.success(t('profile.nothingToSave', 'Nada para salvar.'))
        return
      }
      await updateProfile(body)
      morphToast.success(t('profile.saved', 'Perfil atualizado.'))
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : String(err))
    } finally {
      setProfileSubmitting(false)
    }
  }

  async function handleVerifyPassword(e: FormEvent) {
    e.preventDefault()
    setPasswordError(null)
    if (!currentPassword) {
      setPasswordError(t('profile.currentPasswordRequired', 'Informe a senha atual.'))
      return
    }
    setPasswordSubmitting(true)
    try {
      await authApi.verifyPassword(currentPassword)
      setPasswordStep('change')
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : t('profile.currentPasswordWrong', 'Senha atual incorreta.'))
    } finally {
      setPasswordSubmitting(false)
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault()
    setPasswordError(null)
    if (newPassword !== confirmPassword) {
      setPasswordError(t('profile.passwordMismatch', 'A confirmação de senha não coincide.'))
      return
    }
    if (newPassword.length < 8) {
      setPasswordError(t('profile.passwordTooShort', 'A senha deve ter pelo menos 8 caracteres.'))
      return
    }
    setPasswordSubmitting(true)
    try {
      await updateProfile({ password: newPassword, current_password: currentPassword })
      morphToast.success(t('profile.passwordChanged', 'Senha alterada com sucesso.'))
      cancelPasswordChange()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      // If current password was wrong, go back to step 1
      if (msg.toLowerCase().includes('atual') || msg.toLowerCase().includes('current')) {
        setPasswordStep('verify')
      }
      setPasswordError(msg)
    } finally {
      setPasswordSubmitting(false)
    }
  }

  if (!user) {
    return (
      <SiteShell>
        <div style={containerStyle}>{t('common.loading', 'Aguarde...')}</div>
      </SiteShell>
    )
  }

  return (
    <SiteShell>
      <div style={containerStyle}>
        <div style={cardStyle}>
          <header style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              title={t('common.back', 'Voltar')}
              style={backBtnStyle}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <h1 style={{ margin: 0, fontSize: 22 }}>{t('profile.title', 'Perfil')}</h1>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.6 }}>
                {t('profile.subtitle', 'Atualize seus dados de conta.')}
              </p>
            </div>
          </header>

          {/* Avatar */}
          <div style={avatarSectionStyle}>
            <UserAvatar
              userId={user.id}
              name={user.name ?? ''}
              email={user.email ?? ''}
              size={64}
            />
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                {t('profile.avatarTitle', 'Foto de perfil')}
              </div>
              <div style={{ opacity: 0.65 }}>
                {t(
                  'profile.avatarHint',
                  'Usamos o Gravatar para exibir seu avatar. Para alterar sua foto, acesse',
                )}{' '}
                <a
                  href="https://gravatar.com"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'var(--color-primary, #3b82f6)' }}
                >
                  gravatar.com
                </a>{' '}
                {t('profile.avatarHint2', 'e associe uma imagem ao seu e-mail.')}
              </div>
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border)', margin: '20px 0' }} />

          {/* Profile info */}
          <form onSubmit={handleProfileSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {profileError ? <div style={errorBoxStyle}>{profileError}</div> : null}

            <Field
              label={t('profile.name', 'Nome')}
              value={name}
              onChange={setName}
              maxLength={150}
              required
            />
            <Field
              label="E-mail"
              type="email"
              value={email}
              onChange={setEmail}
              required
            />

            <button
              type="submit"
              disabled={profileSubmitting}
              style={primaryBtnStyle(profileSubmitting)}
            >
              {profileSubmitting
                ? t('common.loading', 'Aguarde...')
                : t('profile.save', 'Salvar alterações')}
            </button>
          </form>

          <div style={{ borderTop: '1px solid var(--border)', margin: '24px 0 20px' }} />

          {/* Password section */}
          {passwordStep === null && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.85, marginBottom: 10 }}>
                {t('profile.passwordSection', 'Senha')}
              </div>
              <button
                type="button"
                onClick={() => setPasswordStep('verify')}
                style={secondaryBtnStyle}
              >
                {t('profile.changePassword', 'Alterar senha')}
              </button>
            </div>
          )}

          {passwordStep === 'verify' && (
            <form onSubmit={handleVerifyPassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.85 }}>
                {t('profile.confirmCurrentPassword', 'Confirme sua senha atual')}
              </div>
              {passwordError ? <div style={errorBoxStyle}>{passwordError}</div> : null}
              <Field
                label={t('profile.currentPassword', 'Senha atual')}
                type="password"
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
                required
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={passwordSubmitting} style={primaryBtnStyle(passwordSubmitting)}>
                  {passwordSubmitting ? t('common.loading', 'Aguarde...') : t('common.confirm', 'Confirmar')}
                </button>
                <button type="button" onClick={cancelPasswordChange} style={secondaryBtnStyle}>
                  {t('common.cancel', 'Cancelar')}
                </button>
              </div>
            </form>
          )}

          {passwordStep === 'change' && (
            <form onSubmit={handlePasswordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.85 }}>
                {t('profile.newPasswordSection', 'Nova senha')}
              </div>
              {passwordError ? <div style={errorBoxStyle}>{passwordError}</div> : null}
              <Field
                label={t('profile.newPassword', 'Nova senha')}
                type="password"
                value={newPassword}
                onChange={setNewPassword}
                minLength={8}
                autoComplete="new-password"
                required
              />
              <Field
                label={t('profile.confirmPassword', 'Confirmar nova senha')}
                type="password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                autoComplete="new-password"
                required
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="submit"
                  disabled={passwordSubmitting}
                  style={primaryBtnStyle(passwordSubmitting)}
                >
                  {passwordSubmitting
                    ? t('common.loading', 'Aguarde...')
                    : t('profile.savePassword', 'Salvar senha')}
                </button>
                <button type="button" onClick={cancelPasswordChange} style={secondaryBtnStyle}>
                  {t('common.cancel', 'Cancelar')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </SiteShell>
  )
}

interface FieldProps {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  required?: boolean
  maxLength?: number
  minLength?: number
  autoComplete?: string
}

const fieldInputStyle: React.CSSProperties = {
  background: 'var(--bg-input)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '10px 12px',
  color: 'var(--text)',
  fontSize: 14,
  outline: 'none',
}

function Field({ label, value, onChange, type = 'text', required, maxLength, minLength, autoComplete }: FieldProps) {
  const shared = {
    value,
    required,
    maxLength,
    minLength,
    autoComplete,
    onChange: (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    style: fieldInputStyle,
  }
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.85 }}>{label}</span>
      {type === 'password' ? <PasswordInput {...shared} /> : <input type={type} {...shared} />}
    </label>
  )
}

const containerStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  padding: '32px 24px',
  background: 'var(--bg-app)',
  color: 'var(--text)',
}

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 520,
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: 32,
}

const avatarSectionStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
}

const backBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text)',
  cursor: 'pointer',
  flexShrink: 0,
}

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.7 : 1,
  }
}

const secondaryBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '10px 18px',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
}

const errorBoxStyle: React.CSSProperties = {
  background: 'var(--color-danger-bg)',
  border: '1px solid var(--color-danger-border)',
  color: 'var(--color-danger)',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13,
}
